/**
 * PLT-7779: Eevo invites, Dropbox style. Each person holds a number of invites and has a personal
 * invite link per account they belong to. When someone new signs up through it and runs their first
 * decision, both get `REFERRAL_REWARD_USD` of credit: the new account, and the account the link was
 * copied from. The credit is booked like a credit grant (`./credit-grants.ts`): granted credit, spent
 * first, not cash and not revenue, and it does not expire.
 *
 * An invite is used up only by a rewarded referral, so links can be shared anywhere and invites are
 * never lost to people who do not sign up. Upright staff set how many invites a person starts with
 * and grant more on the internal invites page.
 *
 * In the code these are "referrals"; "invitation" is an invitation to join an organization
 * (`arvo_invitation`), which is a different thing and never rewarded.
 */
import { isFreeEmailDomain, isTrashEmailDomain } from '../iam/users/email-domains-to-block';

/** What each side of a rewarded referral gets, in US dollars. */
export const REFERRAL_REWARD_USD = 5;

/** The console's query parameter that carries an invite code: `https://eevo.io/?invite=K7Q2MXRA`. */
export const INVITE_QUERY_PARAM = 'invite';

/** Invite codes: 8 characters from an alphabet without look-alikes (no 0/O, 1/I/L). */
export const INVITE_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const INVITE_CODE_LENGTH = 8;
export const INVITE_CODE_PATTERN = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{8}$/;

/** As typed or pasted: case and spaces do not matter. */
export function normalizeInviteCode(text: string): string {
  return text.replace(/\s+/g, '').toUpperCase();
}

/** The default for `defaultInvites` until an admin changes it on the internal invites page. */
export const DEFAULT_INVITES_FOR_NEW_USERS = 3;

/** At most this many invites per grant, and as the default: a typo such as 300 for 3 is refused. */
export const MAX_INVITES_PER_GRANT = 100;

/** The domain of an email address, lower-case; '' when it has none. */
export function referralEmailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at < 0
    ? ''
    : email
        .slice(at + 1)
        .trim()
        .toLowerCase();
}

/**
 * Whether two addresses are from the same company: the same domain, unless it is a free or
 * disposable one (gmail.com and the like), which anyone can have. Only the domain counts: a `+tag`
 * on either address changes nothing.
 */
export function isSameCompany(a: string, b: string): boolean {
  const domain = referralEmailDomain(a);
  return domain !== '' && domain === referralEmailDomain(b) && !isFreeEmailDomain(a) && !isTrashEmailDomain(a);
}

/** An address lower-case and without a `+tag`: jane+eevo@acme.com is jane@acme.com. */
function personKey(email: string): string {
  const lower = email.trim().toLowerCase();
  const at = lower.lastIndexOf('@');
  return at < 0 ? lower : `${lower.slice(0, at).replace(/\+.*$/, '')}${lower.slice(at)}`;
}

/** Why a sign-up through an invite link earns nothing. */
export type ReferralIneligibleReason =
  /** The person had an Eevo account before, of their own or as a member of someone else's. */
  | 'existingUser'
  /** The person who shared the link. */
  | 'self'
  /** The same company as the person who shared the link: they belong in its organization. */
  | 'sameCompany'
  /** Upright's own people are not rewarded. */
  | 'internal'
  /** The person who shared the link had no invites left when the reward came due. */
  | 'noInvitesLeft';

export interface ReferralSignupCheck {
  inviter: { userId: number; email: string; internal: boolean };
  invitee: { userId: number; email: string; internal: boolean; hadAccountBefore: boolean };
}

/**
 * Whether a sign-up through an invite link can be rewarded: null when it can, else why not. Checked
 * when the account is created; whether the person who shared the link still has an invite left is
 * checked when the reward comes due (`noInvitesLeft`).
 */
export function checkReferralSignup({ inviter, invitee }: ReferralSignupCheck): ReferralIneligibleReason | null {
  if (invitee.userId === inviter.userId || personKey(invitee.email) === personKey(inviter.email)) return 'self';
  if (inviter.internal || invitee.internal) return 'internal';
  if (invitee.hadAccountBefore) return 'existingUser';
  if (isSameCompany(invitee.email, inviter.email)) return 'sameCompany';
  return null;
}

/** Invites left: what was granted, less the referrals rewarded. Never below 0. */
export function invitesLeft(granted: number, rewarded: number): number {
  return Math.max(0, granted - rewarded);
}

/** GET /api/arvo/invites: the signed-in person's invites and their link for the account. */
export interface ArvoInvitesResponse {
  /** The link that credits this account. */
  link: string;
  code: string;
  rewardUsd: number;
  invitesLeft: number;
  /** Signed up through one of the person's links, first decision not yet run. */
  pending: number;
  rewarded: number;
}

/** POST /api/arvo/invites/email: the person's link, emailed to someone by Eevo. */
export interface ArvoInviteEmailRequest {
  email: string;
}

/** POST /api/arvo/invites/lookup: what an invite code would give the person signing up with it. */
export interface ArvoInviteLookupRequest {
  code: string;
}

/**
 * `eligible`: the account they create now gets the reward after its first decision; `noInvitesLeft`:
 * the person who shared it has none left; `notEligible`: this person could not be rewarded through
 * it (an existing user, the same company, …); `invalid`: no such code.
 */
export type ArvoInviteLookupStatus = 'eligible' | 'noInvitesLeft' | 'notEligible' | 'invalid';

export interface ArvoInviteLookupResponse {
  status: ArvoInviteLookupStatus;
  rewardUsd: number;
}

export type EevoReferralStatus = 'pending' | 'rewarded' | 'ineligible' | 'cancelled';

/** One sign-up through an invite link, on the internal invites page. */
export interface EevoReferral {
  id: number;
  status: EevoReferralStatus;
  ineligibleReason: ReferralIneligibleReason | null;
  inviterEmail: string;
  inviterOrganizationId: number;
  inviterAccountName: string;
  inviteeEmail: string;
  inviteeOrganizationId: number;
  inviteeAccountName: string;
  createdAt: string;
  rewardedAt: string | null;
}

/** GET /api/admin/arvo-business/invites */
export interface EevoInvitesOverview {
  defaultInvites: number;
  rewardUsd: number;
  totals: { pending: number; rewarded: number; ineligible: number; cancelled: number; creditUsd: number };
  /** Newest first, at most 200. */
  referrals: EevoReferral[];
}

/** PUT /api/admin/arvo-business/invites/settings */
export interface EevoInviteSettingsRequest {
  defaultInvites: number;
}

/** POST /api/admin/arvo-business/invites/grants: invites to one person, or to everyone. */
export interface EevoInviteGrantRequest {
  /** The person's Eevo sign-in email; leave out with `allUsers`. */
  email?: string;
  /** Every Eevo user except Upright's own. */
  allUsers?: boolean;
  count: number;
  /** Why: for the team only, never shown to the person. */
  reason: string;
  /**
   * Email the person that they have new invites. One person only: to tell everyone, send a product
   * update, which records who got it and has an unsubscribe link.
   */
  notify: boolean;
  /** A fresh id per dialog: sending the same request twice grants once. */
  requestId: string;
}

export interface EevoInviteGrantResponse {
  /** How many people got the invites; 0 when this `requestId` had already been granted. */
  granted: number;
  /** Whether the email to the one person went out. */
  emailed: boolean;
  /** For a grant to one person: their invites now. */
  person: { email: string; invitesLeft: number; pending: number; rewarded: number } | null;
}
